/**
 * Emoji set of the picker.
 *
 * A selection covering the real usage of a Discord server rather than the full
 * Unicode table: the latter weighs several hundred kilobytes once the search
 * names are included, for marginal value on a role panel. The server's custom
 * emojis can still be entered in `<:name:id>` form through the picker's free
 * text field.
 *
 * Each entry: `emoji keywords` (keywords lowercase, unaccented).
 */
export interface EmojiCategory {
  key: string;
  /** Clé `client.*` du libellé d'onglet. */
  labelKey: string;
  /**
   * English label, used as the fallback of the `client.emoji*` key derived from
   * `key`. These eight labels were written in French here and shown as the tab
   * tooltips, in every language - a picker whose tabs speak French inside a
   * Japanese dashboard.
   */
  label: string;
  icon: string;
  entries: string[];
}

export const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    key: 'smileys',
    labelKey: 'emojiSmileys',
    label: 'Smileys',
    icon: '😀',
    entries: [
      '😀 sourire content',
      '😃 sourire joie',
      '😄 rire content',
      '😁 sourire dents',
      '😆 rire fort',
      '😅 rire sueur',
      '🤣 mort de rire',
      '😂 pleure de rire',
      '🙂 sourire leger',
      '🙃 tete a lenvers',
      '😉 clin doeil',
      '😊 timide content',
      '😇 ange innocent',
      '🥰 amoureux coeurs',
      '😍 yeux coeurs amour',
      '🤩 etoiles admiration',
      '😘 bisou',
      '😗 bisou neutre',
      '😚 bisou ferme',
      '😋 miam delicieux',
      '😛 langue',
      '😜 langue clin',
      '🤪 fou dingue',
      '😝 langue yeux fermes',
      '🤑 argent riche',
      '🤗 calin',
      '🤭 rire main',
      '🤫 chut silence',
      '🤔 reflexion pense',
      '🤐 bouche fermee',
      '🤨 sourcil doute',
      '😐 neutre',
      '😑 blase',
      '😶 sans bouche',
      '😏 sourire malin',
      '😒 pas content',
      '🙄 yeux au ciel',
      '😬 grimace',
      '🤥 mensonge pinocchio',
      '😌 soulage',
      '😔 triste pensif',
      '😪 fatigue dodo',
      '🤤 bave',
      '😴 dort dodo',
      '😷 masque malade',
      '🤒 fievre malade',
      '🤕 blesse',
      '🤢 degoute nausee',
      '🤮 vomit',
      '🤧 eternue',
      '🥵 chaud',
      '🥶 froid gele',
      '🥴 etourdi',
      '😵 ko etourdi',
      '🤯 explose tete',
      '🤠 cowboy',
      '🥳 fete anniversaire',
      '😎 lunettes cool',
      '🤓 nerd geek',
      '🧐 monocle',
      '😕 confus',
      '😟 inquiet',
      '🙁 leger triste',
      '😮 surpris bouche',
      '😯 etonne',
      '😲 choque',
      '😳 rouge gene',
      '🥺 supplie chibi',
      '😦 fronce',
      '😧 angoisse',
      '😨 peur',
      '😰 anxieux sueur',
      '😥 triste soulage',
      '😢 pleure larme',
      '😭 pleure fort',
      '😱 crie peur',
      '😖 agace',
      '😣 persevere',
      '😞 decu',
      '😓 sueur froide',
      '😩 fatigue las',
      '😫 epuise',
      '🥱 baille',
      '😤 enerve vapeur',
      '😡 colere rouge',
      '😠 fache',
      '🤬 jure grossier',
      '😈 diable malin',
      '👿 demon colere',
      '💀 crane mort',
      '☠️ tete de mort',
      '💩 caca',
      '🤡 clown',
      '👹 ogre',
      '👺 goblin',
      '👻 fantome',
      '👽 alien extraterrestre',
      '🤖 robot bot',
      '🎃 citrouille halloween',
    ],
  },
  {
    key: 'people',
    labelKey: 'emojiPeople',
    label: 'People',
    icon: '👋',
    entries: [
      '👋 salut coucou main',
      '🤚 main levee',
      '🖐️ main doigts',
      '✋ stop main',
      '🖖 vulcain',
      '👌 ok parfait',
      '🤌 doigts italien',
      '🤏 pincee petit',
      '✌️ victoire paix',
      '🤞 doigts croises chance',
      '🤟 amour signe',
      '🤘 rock corne',
      '🤙 appelle moi',
      '👈 gauche',
      '👉 droite',
      '👆 haut',
      '👇 bas',
      '☝️ index haut',
      '👍 pouce haut bien oui',
      '👎 pouce bas mal non',
      '✊ poing',
      '👊 poing avant',
      '🤛 poing gauche',
      '🤜 poing droit',
      '👏 applaudir bravo',
      '🙌 bras leves youpi',
      '👐 mains ouvertes',
      '🤲 paumes',
      '🤝 poignee de main accord',
      '🙏 priere merci stp',
      '✍️ ecrire',
      '💪 muscle force',
      '🦾 bras robot',
      '🧠 cerveau',
      '👀 yeux regarde',
      '👁️ oeil',
      '👶 bebe',
      '🧒 enfant',
      '👦 garcon',
      '👧 fille',
      '🧑 personne',
      '👨 homme',
      '👩 femme',
      '🧓 senior',
      '👮 police',
      '🕵️ detective',
      '💂 garde',
      '👷 ouvrier chantier',
      '🤴 prince',
      '👸 princesse',
      '🧙 magicien mage',
      '🧚 fee',
      '🧛 vampire',
      '🧟 zombie',
      '🦸 super heros',
      '🦹 super vilain',
      '🎅 pere noel',
      '🤶 mere noel',
      '🧑‍💻 developpeur code',
      '🧑‍🎤 chanteur',
      '💃 danseuse',
      '🕺 danseur',
      '🧘 meditation zen',
      '🏃 court course',
      '🚶 marche',
      '🤷 hausse epaules sais pas',
      '🤦 facepalm honte',
      '🙋 leve la main',
      '🙆 ok bras',
      '🙅 non interdit',
      '💁 information accueil',
      '🙇 salut respect',
    ],
  },
  {
    key: 'nature',
    labelKey: 'emojiNature',
    label: 'Nature',
    icon: '🌿',
    entries: [
      '🐶 chien toutou',
      '🐱 chat minou',
      '🐭 souris',
      '🐹 hamster',
      '🐰 lapin',
      '🦊 renard',
      '🐻 ours',
      '🐼 panda',
      '🐨 koala',
      '🐯 tigre',
      '🦁 lion',
      '🐮 vache',
      '🐷 cochon',
      '🐸 grenouille',
      '🐵 singe',
      '🙈 singe yeux',
      '🙉 singe oreilles',
      '🙊 singe bouche',
      '🐒 singe queue',
      '🐔 poule',
      '🐧 pingouin',
      '🐦 oiseau',
      '🐤 poussin',
      '🦆 canard',
      '🦅 aigle',
      '🦉 hibou chouette',
      '🦇 chauve souris',
      '🐺 loup',
      '🐗 sanglier',
      '🐴 cheval',
      '🦄 licorne',
      '🐝 abeille',
      '🐛 chenille',
      '🦋 papillon',
      '🐌 escargot',
      '🐞 coccinelle',
      '🐜 fourmi',
      '🕷️ araignee',
      '🦂 scorpion',
      '🐢 tortue',
      '🐍 serpent',
      '🦎 lezard',
      '🦖 dinosaure trex',
      '🐙 poulpe pieuvre',
      '🦑 calmar',
      '🦀 crabe',
      '🐡 poisson',
      '🐠 poisson tropical',
      '🐟 poisson',
      '🐬 dauphin',
      '🐳 baleine',
      '🦈 requin',
      '🐊 crocodile',
      '🐅 tigre',
      '🦓 zebre',
      '🦍 gorille',
      '🐘 elephant',
      '🦏 rhinoceros',
      '🐪 chameau',
      '🦒 girafe',
      '🐄 vache',
      '🐖 cochon',
      '🐑 mouton',
      '🐐 chevre',
      '🌵 cactus',
      '🌲 sapin arbre',
      '🌳 arbre',
      '🌴 palmier',
      '🌱 pousse plante',
      '🌿 herbe feuille',
      '☘️ trefle',
      '🍀 trefle chance',
      '🍁 erable automne',
      '🍂 feuilles mortes',
      '🍃 feuille vent',
      '🌷 tulipe',
      '🌹 rose fleur',
      '🥀 fleur fanee',
      '🌺 hibiscus',
      '🌸 fleur cerisier sakura',
      '🌼 marguerite',
      '🌻 tournesol',
      '🌞 soleil visage',
      '🌝 lune visage',
      '🌚 nouvelle lune',
      '🌜 lune',
      '🌛 lune',
      '🌙 croissant lune',
      '⭐ etoile',
      '🌟 etoile brillante',
      '✨ etincelles magie',
      '⚡ eclair foudre',
      '🔥 feu flamme hot',
      '💥 explosion boom',
      '❄️ neige flocon froid',
      '🌈 arc en ciel',
      '☀️ soleil',
      '⛅ nuage soleil',
      '☁️ nuage',
      '🌧️ pluie',
      '⛈️ orage',
      '🌊 vague mer eau',
      '💧 goutte eau',
      '🌍 terre monde globe',
    ],
  },
  {
    key: 'food',
    labelKey: 'emojiFood',
    label: 'Food',
    icon: '🍕',
    entries: [
      '🍏 pomme verte',
      '🍎 pomme rouge',
      '🍐 poire',
      '🍊 orange',
      '🍋 citron',
      '🍌 banane',
      '🍉 pasteque',
      '🍇 raisin',
      '🍓 fraise',
      '🫐 myrtille',
      '🍈 melon',
      '🍒 cerise',
      '🍑 peche',
      '🥭 mangue',
      '🍍 ananas',
      '🥥 noix de coco',
      '🥝 kiwi',
      '🍅 tomate',
      '🍆 aubergine',
      '🥑 avocat',
      '🥦 brocoli',
      '🥬 salade',
      '🥒 concombre',
      '🌶️ piment epice',
      '🌽 mais',
      '🥕 carotte',
      '🥔 pomme de terre',
      '🍠 patate douce',
      '🥐 croissant',
      '🥖 baguette pain',
      '🍞 pain',
      '🧀 fromage',
      '🥚 oeuf',
      '🍳 oeuf plat',
      '🥞 pancakes',
      '🧇 gaufre',
      '🥓 bacon',
      '🍔 burger hamburger',
      '🍟 frites',
      '🍕 pizza',
      '🌭 hotdog',
      '🥪 sandwich',
      '🌮 taco',
      '🌯 burrito',
      '🥗 salade',
      '🍝 pates spaghetti',
      '🍜 ramen nouilles',
      '🍲 soupe',
      '🍣 sushi',
      '🍱 bento',
      '🍚 riz',
      '🍙 onigiri',
      '🍦 glace',
      '🍩 donut beignet',
      '🍪 cookie biscuit',
      '🎂 gateau anniversaire',
      '🍰 part de gateau',
      '🧁 cupcake',
      '🍫 chocolat',
      '🍬 bonbon',
      '🍭 sucette',
      '🍯 miel',
      '🍿 popcorn',
      '🥤 boisson soda',
      '☕ cafe',
      '🍵 the',
      '🧃 jus',
      '🍺 biere',
      '🍻 bieres trinquer',
      '🍷 vin',
      '🥂 champagne trinquer',
      '🍾 champagne',
      '🥛 lait',
      '🧊 glacon',
      '🍽️ assiette repas',
      '🥄 cuillere',
    ],
  },
  {
    key: 'activity',
    labelKey: 'emojiActivity',
    label: 'Activities',
    icon: '⚽',
    entries: [
      '⚽ football foot',
      '🏀 basket',
      '🏈 football americain',
      '⚾ baseball',
      '🎾 tennis',
      '🏐 volley',
      '🏉 rugby',
      '🎱 billard',
      '🏓 ping pong',
      '🏸 badminton',
      '🥅 but cage',
      '🏒 hockey',
      '🏑 hockey gazon',
      '🥍 crosse',
      '🏏 cricket',
      '⛳ golf',
      '🏹 tir a larc',
      '🎣 peche',
      '🥊 boxe',
      '🥋 arts martiaux judo',
      '🎽 course maillot',
      '⛸️ patinage',
      '🎿 ski',
      '🛷 luge',
      '🏂 snowboard',
      '🏋️ musculation halterophilie',
      '🤸 gymnastique',
      '🤺 escrime',
      '🤼 lutte',
      '🏊 natation nage',
      '🏄 surf',
      '🚴 velo cyclisme',
      '🏆 trophee gagnant victoire',
      '🥇 medaille or premier',
      '🥈 medaille argent',
      '🥉 medaille bronze',
      '🎖️ medaille militaire',
      '🏅 medaille sport',
      '🎯 cible objectif',
      '🎮 manette jeu gaming',
      '🕹️ joystick arcade',
      '🎲 de hasard',
      '♟️ echecs',
      '🧩 puzzle',
      '🎰 machine a sous casino',
      '🎭 theatre masques',
      '🎨 peinture art',
      '🎤 micro chant karaoke',
      '🎧 casque audio musique',
      '🎼 partition',
      '🎵 note musique',
      '🎶 notes musique',
      '🎹 piano clavier',
      '🥁 batterie tambour',
      '🎸 guitare',
      '🎺 trompette',
      '🎻 violon',
      '🎬 cinema clap film',
      '🎪 cirque',
      '🎫 ticket billet',
    ],
  },
  {
    key: 'travel',
    labelKey: 'emojiTravel',
    label: 'Travel',
    icon: '🚀',
    entries: [
      '🚗 voiture auto',
      '🚕 taxi',
      '🚙 suv',
      '🚌 bus',
      '🚎 trolley',
      '🏎️ formule 1 course',
      '🚓 police voiture',
      '🚑 ambulance',
      '🚒 pompier camion',
      '🚐 van',
      '🚚 camion',
      '🚛 semi remorque',
      '🚜 tracteur',
      '🛴 trottinette',
      '🚲 velo',
      '🛵 scooter',
      '🏍️ moto',
      '🚨 gyrophare alerte urgence',
      '🚔 police',
      '🚍 bus',
      '🚂 train locomotive',
      '🚆 train',
      '🚇 metro',
      '🚊 tram',
      '🚁 helicoptere',
      '✈️ avion',
      '🛫 decollage',
      '🛬 atterrissage',
      '🚀 fusee lancement boost',
      '🛸 ovni soucoupe',
      '⛵ voilier bateau',
      '🚤 hors bord',
      '🛥️ yacht',
      '🚢 navire bateau',
      '⚓ ancre',
      '🗺️ carte',
      '🧭 boussole',
      '🏔️ montagne',
      '🌋 volcan',
      '🏕️ camping tente',
      '🏖️ plage',
      '🏜️ desert',
      '🏝️ ile',
      '🏟️ stade',
      '🏛️ monument',
      '🏗️ chantier construction',
      '🏠 maison',
      '🏡 maison jardin',
      '🏢 bureau immeuble',
      '🏬 magasin',
      '🏦 banque',
      '🏥 hopital',
      '🏫 ecole',
      '🏰 chateau',
      '⛩️ torii',
      '🗼 tour tokyo',
      '🗽 statue liberte',
      '🎡 grande roue',
      '🎢 montagnes russes',
      '🌃 ville nuit',
      '🌆 ville coucher soleil',
      '🌉 pont',
      '🌌 voie lactee',
      '🎇 feu dartifice',
    ],
  },
  {
    key: 'objects',
    labelKey: 'emojiObjects',
    label: 'Objects',
    icon: '💡',
    entries: [
      '⌚ montre',
      '📱 telephone mobile',
      '💻 ordinateur portable pc',
      '⌨️ clavier',
      '🖥️ ordinateur ecran',
      '🖨️ imprimante',
      '🖱️ souris',
      '💽 disque',
      '💾 disquette sauvegarde',
      '💿 cd',
      '📀 dvd',
      '📷 appareil photo',
      '📸 photo flash',
      '📹 camera video',
      '🎥 camera cinema',
      '📺 television tv',
      '📻 radio',
      '🎙️ micro studio',
      '⏱️ chronometre',
      '⏰ reveil alarme',
      '⏳ sablier temps',
      '📡 antenne satellite',
      '🔋 batterie',
      '🔌 prise electrique',
      '💡 ampoule idee',
      '🔦 lampe torche',
      '🕯️ bougie',
      '🧯 extincteur',
      '🛢️ baril',
      '💸 argent billets',
      '💵 dollar billet',
      '💰 sac dargent',
      '💳 carte bancaire paiement',
      '🧾 recu facture',
      '⚖️ balance justice',
      '🔧 cle a molette outil',
      '🔨 marteau',
      '⚒️ outils',
      '🛠️ outils reparation',
      '⛏️ pioche',
      '🔩 boulon vis',
      '⚙️ engrenage parametres config',
      '🧰 boite a outils',
      '🧲 aimant',
      '🔫 pistolet',
      '💣 bombe',
      '🔪 couteau',
      '🗡️ epee',
      '⚔️ epees combat',
      '🛡️ bouclier protection securite',
      '🚬 cigarette',
      '⚰️ cercueil',
      '🔮 boule de cristal',
      '📿 chapelet',
      '💈 barbier',
      '🔭 telescope',
      '🔬 microscope',
      '🕳️ trou',
      '💊 medicament pilule',
      '💉 seringue',
      '🩸 sang',
      '🧬 adn',
      '🦠 microbe virus',
      '🧪 eprouvette test',
      '🌡️ thermometre',
      '🧹 balai nettoyage',
      '🧺 panier',
      '🧻 papier toilette',
      '🚽 toilettes',
      '🚿 douche',
      '🛁 baignoire',
      '🔑 cle',
      '🗝️ vieille cle',
      '🚪 porte',
      '🛋️ canape',
      '🛏️ lit',
      '🖼️ cadre image',
      '🛍️ shopping sacs',
      '🎁 cadeau',
      '🎈 ballon fete',
      '🎉 fete confettis',
      '🎊 confettis',
      '🎀 noeud ruban',
      '🎗️ ruban',
      '✉️ enveloppe mail',
      '📧 email',
      '📨 message recu',
      '📩 message envoye',
      '📤 sortie envoi',
      '📥 entree reception',
      '📦 colis paquet',
      '📪 boite aux lettres',
      '📢 megaphone annonce',
      '📣 haut parleur annonce',
      '📯 cor',
      '🔔 cloche notification',
      '🔕 cloche barree muet',
      '📜 parchemin',
      '📃 document page',
      '📄 page',
      '📑 marque pages',
      '📊 graphique barres stats',
      '📈 courbe hausse',
      '📉 courbe baisse',
      '🗂️ dossiers',
      '📅 calendrier date',
      '📆 calendrier',
      '🗒️ bloc notes',
      '📇 fiches',
      '📁 dossier',
      '📂 dossier ouvert',
      '🗃️ boite fichiers',
      '📋 presse papier liste',
      '📌 punaise epingle',
      '📍 position lieu',
      '📎 trombone',
      '✂️ ciseaux',
      '📏 regle',
      '📐 equerre',
      '🔒 verrou ferme prive',
      '🔓 verrou ouvert',
      '🔐 verrou cle',
      '🔏 verrou stylo',
      '🖊️ stylo',
      '✏️ crayon',
      '📝 note ecrire',
      '📚 livres',
      '📖 livre ouvert',
      '🔖 marque page',
      '🏷️ etiquette tag',
      '🔍 loupe recherche',
      '🔎 loupe',
    ],
  },
  {
    key: 'symbols',
    labelKey: 'emojiSymbols',
    label: 'Symbols',
    icon: '✅',
    entries: [
      '❤️ coeur rouge amour',
      '🧡 coeur orange',
      '💛 coeur jaune',
      '💚 coeur vert',
      '💙 coeur bleu',
      '💜 coeur violet',
      '🖤 coeur noir',
      '🤍 coeur blanc',
      '🤎 coeur marron',
      '💔 coeur brise',
      '❣️ coeur exclamation',
      '💕 deux coeurs',
      '💞 coeurs tournent',
      '💓 coeur battant',
      '💗 coeur grandit',
      '💖 coeur brillant',
      '💘 coeur fleche',
      '💝 coeur cadeau',
      '💟 decoration coeur',
      '☮️ paix',
      '✝️ croix',
      '☪️ croissant islam',
      '🕉️ om',
      '☸️ dharma',
      '✡️ etoile david',
      '🔯 etoile',
      '☯️ yin yang',
      '⛎ ophiuchus',
      '♈ belier',
      '♉ taureau',
      '♊ gemeaux',
      '♋ cancer',
      '♌ lion',
      '♍ vierge',
      '♎ balance',
      '♏ scorpion',
      '♐ sagittaire',
      '♑ capricorne',
      '♒ verseau',
      '♓ poissons',
      '🆔 identite id',
      '⚛️ atome',
      '🉑 accepte',
      '☢️ radioactif',
      '☣️ biohazard danger',
      '📴 telephone eteint',
      '📳 vibreur',
      '🈶 payant',
      '🆚 versus vs',
      '💮 fleur blanche',
      '🉐 bonne affaire',
      '㊙️ secret',
      '🈲 interdit',
      '🅰️ a',
      '🅱️ b',
      '🆎 ab',
      '🆑 cl',
      '🅾️ o',
      '🆘 sos aide urgence',
      '❌ croix non erreur',
      '⭕ cercle rond',
      '🛑 stop arret',
      '⛔ interdit',
      '📛 badge nom',
      '🚫 interdit non',
      '💯 cent parfait',
      '💢 colere',
      '♨️ source chaude',
      '🚷 pietons interdits',
      '🚯 ne pas jeter',
      '🚳 velos interdits',
      '🚱 eau non potable',
      '🔞 18 ans interdit mineurs',
      '📵 telephones interdits',
      '❗ exclamation attention',
      '❕ exclamation blanc',
      '❓ question',
      '❔ question blanc',
      '‼️ double exclamation',
      '⁉️ exclamation question',
      '🔅 luminosite faible',
      '🔆 luminosite forte',
      '〽️ alternance',
      '⚠️ attention avertissement danger',
      '🚸 enfants',
      '🔱 trident',
      '⚜️ fleur de lys',
      '🔰 debutant',
      '♻️ recyclage',
      '✅ coche valide oui ok',
      '☑️ case cochee',
      '✔️ coche',
      '🔵 rond bleu',
      '🔴 rond rouge',
      '🟠 rond orange',
      '🟡 rond jaune',
      '🟢 rond vert',
      '🟣 rond violet',
      '⚫ rond noir',
      '⚪ rond blanc',
      '🟥 carre rouge',
      '🟧 carre orange',
      '🟨 carre jaune',
      '🟩 carre vert',
      '🟦 carre bleu',
      '🟪 carre violet',
      '⬛ carre noir',
      '⬜ carre blanc',
      '🔶 losange orange',
      '🔷 losange bleu',
      '🔸 petit losange',
      '🔹 petit losange bleu',
      '🔺 triangle rouge',
      '🔻 triangle bas',
      '💠 diamant',
      '🔘 bouton radio',
      '🔳 bouton blanc',
      '🔲 bouton noir',
      '➕ plus ajouter',
      '➖ moins retirer',
      '➗ division',
      '✖️ multiplication',
      '💲 dollar',
      '💱 change devises',
      '™️ marque',
      '©️ copyright',
      '®️ marque deposee',
      '〰️ vague',
      '➰ boucle',
      '🔚 fin',
      '🔙 retour',
      '🔛 on',
      '🔝 haut top',
      '🔜 bientot',
      '✳️ asterisque',
      '❇️ etincelle',
      '🔀 aleatoire shuffle',
      '🔁 repeter boucle',
      '🔂 repeter une fois',
      '▶️ lecture play',
      '⏸️ pause',
      '⏹️ stop',
      '⏺️ enregistrer rec',
      '⏭️ suivant',
      '⏮️ precedent',
      '⏩ avance rapide',
      '⏪ retour rapide',
      '🔼 haut',
      '🔽 bas',
      '⬆️ fleche haut',
      '⬇️ fleche bas',
      '➡️ fleche droite',
      '⬅️ fleche gauche',
      '↗️ diagonale',
      '↘️ diagonale',
      '🔄 actualiser rafraichir',
      '🏳️ drapeau blanc',
      '🏴 drapeau noir',
      '🏁 damier arrivee',
      '🚩 drapeau marque',
      '🏳️‍🌈 arc en ciel lgbt fierte',
      '🎌 drapeaux croises',
    ],
  },
];

export interface EmojiEntry {
  emoji: string;
  keywords: string;
  category: string;
}

/** A flat index, built once, for searching. */
export const ALL_EMOJI: EmojiEntry[] = EMOJI_CATEGORIES.flatMap((category) =>
  category.entries.map((entry) => {
    const separator = entry.indexOf(' ');
    return {
      emoji: entry.slice(0, separator),
      keywords: entry.slice(separator + 1),
      category: category.key,
    };
  }),
);
